import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Circle, Eye, EyeOff } from 'lucide-react';
import { API_URL } from '../apiConfig';


const AdminResetPassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [email, setEmail] = useState('');

  const [otp, setOtp] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    setEmail(searchParams.get('email') || '');
    setOtp(searchParams.get('otp') || '');
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    if (!newPassword) {
      return setMessage('Please enter a new password');
    }
    if (!confirm) {
      return setMessage('Please confirm your new password');
    }
    if (newPassword.length < 8 || newPassword.length > 20 || !/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      return setMessage('Password must be 8 to 20 characters long and include 1 uppercase letter, 1 lowercase letter, and 1 number.');
    }
    if (newPassword !== confirm) return setMessage('Passwords do not match');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin-reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp, newPassword })
      });
      const data = await res.json();
      if (!res.ok) {
        setMessage(data.message || 'Failed to reset password');
      } else {
        setMessage('Password changed successfully.');
        setTimeout(() => navigate('/admin-login'), 1500);
      }
    } catch (err) {
      console.error(err);
      setMessage('Server error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const PasswordRequirements = ({ value }) => {
    const checks = [
      { label: "8 to 20 characters", fulfilled: value.length >= 8 && value.length <= 20 },
      { label: "At least one uppercase letter", fulfilled: /[A-Z]/.test(value) },
      { label: "At least one lowercase letter", fulfilled: /[a-z]/.test(value) },
      { label: "At least one number", fulfilled: /[0-9]/.test(value) },
    ];

    return (
      <div style={styles.checklistContainer}>
        {checks.map((check, index) => (
          <div key={index} style={styles.checkItem}>
            {check.fulfilled ? (
              <Check size={14} color="#10b981" strokeWidth={3} />
            ) : (
              <Circle size={14} color="#9ca3af" />
            )}
            <span style={{
              ...styles.checkLabel,
              color: check.fulfilled ? "#10b981" : "#6b7280",
              fontWeight: check.fulfilled ? "600" : "400"
            }}>
              {check.label}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={styles.page}>
      <header style={styles.topHeader}>
        <img src={require("../assets/logonobg.png")} alt="Logo" style={styles.logo} />
      </header>

      <div style={styles.content}>
        <div style={styles.card}>
          <div style={styles.iconContainer}>
            <div style={styles.iconCircle}>
              <span style={styles.icon}>🛡️</span>
            </div>
          </div>
          <h2 style={styles.titleMain}>Reset Password</h2>
          <p style={styles.subtitle}>Set a new secure password for <br />
            <strong style={{ color: "#2E4B4C", fontSize: "15px" }}>{email}</strong>
          </p>

          <form onSubmit={handleSubmit}>
            <div style={{ textAlign: "left", marginBottom: "8px" }}>
              <label style={styles.label}>New Password</label>
            </div>
            <div style={styles.passwordWrapper}>
              <input
                type={showNewPassword ? "text" : "password"}
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={styles.input}
              />
              <button
                type="button"
                style={styles.passwordToggle}
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            <PasswordRequirements value={newPassword} />

            <div style={{ textAlign: "left", marginBottom: "8px", marginTop: "16px" }}>
              <label style={styles.label}>Confirm Password</label>
            </div>
            <div style={styles.passwordWrapper}>
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                style={styles.input}
              />
              <button
                type="button"
                style={styles.passwordToggle}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {message && (
              <div style={{
                ...(message.includes('successful') ? styles.successText : styles.errorText), 
                textAlign: 'left'
              }}>
                {message}
              </div>
            )}

            <button type="submit" style={styles.button} disabled={loading}>
              {loading ? 'Saving...' : 'Save Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#f9fafb",
    fontFamily: "'Montserrat', sans-serif",
  },
  topHeader: {
    backgroundColor: '#14291F',
    height: '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
  },
  logo: {
    height: '45px',
    objectFit: 'contain'
  },
  content: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "60px 20px",
  },
  card: {
    width: '100%',
    maxWidth: '440px',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    border: '1px solid #e5e7eb',
    padding: '48px 40px',
    textAlign: 'center',
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 10px 10px -5px rgba(0, 0, 0, 0.02)",
  },
  iconContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "24px",
  },
  iconCircle: {
    width: "80px",
    height: "80px",
    backgroundColor: "#ecfdf5",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  icon: {
    fontSize: "36px",
  },
  titleMain: {
    fontFamily: "'Abril Fatface', serif",
    fontSize: '32px',
    color: '#2E4B4C',
    margin: '0 0 12px 0',
    letterSpacing: "0.5px"
  },
  subtitle: {
    fontSize: '14px',
    color: '#6b7280',
    marginBottom: '32px',
    lineHeight: '1.6',
  },
  label: {
    color: "#4b5563",
    fontWeight: "600",
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "1px",
    marginBottom: "8px",
    display: "inline-block"
  },
  input: {
    width: "100%",
    padding: "14px 16px",
    fontSize: "15px",
    border: "2px solid #e5e7eb",
    borderRadius: "12px",
    boxSizing: "border-box",
    fontWeight: "500",
    color: "#14291F",
    outline: "none",
    transition: "all 0.2s",
    backgroundColor: "#f9fafb"
  },
  passwordWrapper: {
    position: 'relative',
    width: '100%',
    marginBottom: "4px"
  },
  passwordToggle: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#9ca3af',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    borderRadius: '40px'
  },
  button: {
    backgroundColor: "#14291F",
    color: "#fff",
    padding: "16px 30px",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "700",
    width: "100%",
    marginTop: "24px",
    transition: "all 0.2s",
    textTransform: "uppercase",
    letterSpacing: "1px",
    boxShadow: "0 4px 12px rgba(20, 41, 31, 0.15)"
  },
  errorText: {
    color: "#e74c3c",
    fontSize: "12px",
    marginTop: "8px",
    marginBottom: "8px",
    fontWeight: "500",
    letterSpacing: "0.3px"
  },
  successText: {
    color: "#2E4B4C",
    fontSize: "13px",
    marginTop: "8px",
    marginBottom: "8px",
    fontWeight: "500",
    padding: "12px",
    backgroundColor: "#f0fdf4",
    borderRadius: "8px",
    border: "1px solid #bbf7d0"
  },
  checklistContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '12px',
    padding: '12px',
    backgroundColor: "#f8fafc",
    borderRadius: "10px",
    border: "1px solid #f1f5f9"
  },
  checkItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  checkLabel: {
    fontSize: '12px',
    fontWeight: "500"
  }
};

export default AdminResetPassword;

